import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Loader2, Search, User, Repeat, MapPin, DollarSign, ChevronRight } from 'lucide-react';
import { fetchAdminCustomers } from '@/lib/api';
import CustomerDetailDrawer from '@/components/admin/CustomerDetailDrawer';

const AUD = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
};

export default function CustomersPanel() {
  const [q, setQ] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const { data: customers = [], isLoading } = useSWR(
    ['admin-customers', q],
    () => fetchAdminCustomers({ q, limit: 500 }),
    { keepPreviousData: true },
  );

  const totalSpend = useMemo(
    () => customers.reduce((s, c) => s + (Number(c.lifetime_spend) || 0), 0),
    [customers],
  );

  return (
    <div data-testid="admin-customers">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">
            {customers.length} customers · {AUD(totalSpend)} lifetime
          </div>
          <h2 className="font-display font-light text-3xl sm:text-4xl mt-2">Customers</h2>
        </div>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email"
          data-testid="admin-customers-search"
          className="w-full bg-transparent border border-white/15 focus:border-[var(--mw-primary)] outline-none pl-9 pr-4 py-2.5 text-sm text-white transition-colors"
        />
      </div>

      {isLoading && customers.length === 0 ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
      ) : customers.length === 0 ? (
        <div className="border border-white/10 py-20 text-center text-white/50 text-sm uppercase tracking-[0.24em]">
          No customers
        </div>
      ) : (
        <div className="border border-white/10">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.28em] text-white/45 bg-white/[0.02]">
            <div className="col-span-4">Customer</div>
            <div className="col-span-3">Location</div>
            <div className="col-span-1 text-center">Orders</div>
            <div className="col-span-2 text-right">Lifetime spend</div>
            <div className="col-span-2 text-right">Last order</div>
          </div>
          {customers.map((c) => (
            <button
              type="button"
              key={c.email}
              onClick={() => setSelectedEmail(c.email)}
              data-testid={`admin-customer-${c.email}`}
              className="w-full text-left grid grid-cols-12 gap-4 items-center px-5 py-4 border-t border-white/5 hover:bg-white/[0.03] focus:bg-white/[0.05] outline-none transition-colors first:border-t-0"
            >
              <div className="col-span-12 md:col-span-4 flex items-center gap-3">
                <div className="w-9 h-9 border border-white/10 flex items-center justify-center text-white/50 shrink-0">
                  <User size={14} />
                </div>
                <div className="min-w-0">
                  <div className="text-white truncate">{c.name || '—'}</div>
                  <div className="text-[11px] text-white/45 truncate">{c.email}</div>
                </div>
                {c.has_subscription && (
                  <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] text-emerald-400 border border-emerald-500/30 px-2 py-1 shrink-0">
                    <Repeat size={10} /> Sub
                  </span>
                )}
              </div>
              <div className="col-span-6 md:col-span-3 text-[11px] uppercase tracking-[0.22em] text-white/55 truncate">
                {c.suburbs?.length ? (
                  <span className="inline-flex items-center gap-1.5"><MapPin size={11} /> {c.suburbs.join(' · ')}</span>
                ) : (
                  <span className="text-white/25">—</span>
                )}
              </div>
              <div className="col-span-2 md:col-span-1 text-center text-white tabular-nums">
                {c.orders_count}
                {c.paid_orders_count !== c.orders_count && (
                  <span className="text-white/35 text-xs"> ({c.paid_orders_count} paid)</span>
                )}
              </div>
              <div className="col-span-4 md:col-span-2 text-right font-display text-lg text-white tabular-nums">
                {AUD(c.lifetime_spend)}
              </div>
              <div className="col-span-12 md:col-span-2 text-right text-[11px] uppercase tracking-[0.22em] text-white/55 flex items-center justify-end gap-2">
                {fmtDate(c.last_order_at)}
                <ChevronRight size={12} className="text-white/30" />
              </div>
            </button>
          ))}
        </div>
      )}
      {/* Detail drawer */}
      <CustomerDetailDrawer email={selectedEmail} onClose={() => setSelectedEmail(null)} />
      {/* Unused imports silencer */}
      <DollarSign className="hidden" />
    </div>
  );
}
